import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { Clock, Mail, Hash, MessageSquare } from 'lucide-react';
import { brandService } from '@/services/brand.service';
import { AgencyBrandRequest, RespondToBrandRequestPayload } from '@/services/agency.service';
import { toast } from 'react-hot-toast';

export function AgencyRequestsManager() {
  const [requests, setRequests] = useState<AgencyBrandRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<AgencyBrandRequest | null>(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [responding, setResponding] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const data = await brandService.getBrandPendingRequests();
      setRequests(data);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
      toast.error('Failed to load partner requests');
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = (request: AgencyBrandRequest) => {
    setSelectedRequest(request);
    setResponseMessage('');
    setShowResponseModal(true);
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;

    try {
      setResponding(true);
      const payload: RespondToBrandRequestPayload = {
        status: 'APPROVED',
        responseMessage: responseMessage.trim() || undefined,
      };

      await brandService.respondToBrandRequest(selectedRequest.id, payload);
      
      toast.success(`Approved ${selectedRequest.agency.firstName} ${selectedRequest.agency.lastName} as your partner!`);
      
      // Update local state
      setRequests(prev => prev.filter(r => r.id !== selectedRequest.id));
      setShowResponseModal(false);
      setSelectedRequest(null);
      setResponseMessage('');
    } catch (error) {
      console.error('Failed to approve request:', error);
      toast.error('Failed to approve request. Please try again.');
    } finally {
      setResponding(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;

    try {
      setResponding(true);
      const payload: RespondToBrandRequestPayload = {
        status: 'REJECTED',
        responseMessage: responseMessage.trim() || undefined,
      };

      await brandService.respondToBrandRequest(selectedRequest.id, payload);
      
      toast.success(`Rejected ${selectedRequest.agency.firstName} ${selectedRequest.agency.lastName}'s request.`);
      
      // Update local state
      setRequests(prev => prev.filter(r => r.id !== selectedRequest.id));
      setShowResponseModal(false);
      setSelectedRequest(null);
      setResponseMessage('');
    } catch (error) {
      console.error('Failed to reject request:', error);
      toast.error('Failed to reject request. Please try again.');
    } finally {
      setResponding(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-muted rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Pending Partner Requests</h3>
          <p className="text-muted-foreground">
            You don't have any pending requests from partners at the moment.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-foreground">Pending Partner Requests</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchRequests}
            className="text-sm"
          >
            Refresh
          </Button>
        </div>

        {requests.map((request) => (
          <Card key={request.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-yellow-500" />
                    <CardTitle className="text-lg">
                      {request.agency.firstName} {request.agency.lastName}
                    </CardTitle>
                    <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Pending</Badge>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>Requested {formatDate(request.createdAt)}</span>
                  </div>
                </div>
                <Button
                  onClick={() => handleRespond(request)}
                  size="sm"
                  className="bg-brand-600 hover:bg-brand-700"
                >
                  Respond
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-3">
                {/* Partner Details */}
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    {request.agency.email}
                  </div>
                  <div className="flex items-center gap-1">
                    <Hash className="w-3 h-3" />
                    Code: {request.agency.code}
                  </div>
                </div>

                {/* Partner Message */}
                {request.message && (
                  <div className="bg-muted p-3 rounded-lg">
                    <p className="text-sm font-medium text-muted-foreground mb-1">Partner Message:</p>
                    <p className="text-sm text-muted-foreground">{request.message}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={() => handleRespond(request)}
                    size="sm"
                    variant="outline"
                    className="flex-1"
                  >
                    Review & Respond
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Response Modal */}
      <Transition show={showResponseModal} as={Fragment}>
        <Dialog onClose={() => setShowResponseModal(false)} className="relative z-50">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md bg-card rounded-lg shadow-xl">
                <div className="p-6">
                  <Dialog.Title className="text-lg font-semibold mb-4">Respond to Partner Request</Dialog.Title>
                  
                  {selectedRequest && (
                    <div className="space-y-4">
                      <div className="bg-muted p-3 rounded-lg">
                        <p className="text-sm font-medium text-muted-foreground mb-1">Partner:</p>
                        <p className="text-sm text-muted-foreground">
                          {selectedRequest.agency.firstName} {selectedRequest.agency.lastName} ({selectedRequest.agency.code})
                        </p>
                        {selectedRequest.message && (
                          <>
                            <p className="text-sm font-medium text-muted-foreground mb-1 mt-2">Their Message:</p>
                            <p className="text-sm text-muted-foreground">{selectedRequest.message}</p>
                          </>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-muted-foreground mb-2">
                          Response Message (Optional)
                        </label>
                        <textarea
                          placeholder="Provide feedback or instructions for the partner..."
                          value={responseMessage}
                          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setResponseMessage(e.target.value)}
                          rows={3}
                          className="resize-none w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                        />
                      </div>

                      <div className="flex gap-2 pt-2">
                        <Button
                          onClick={handleApprove}
                          disabled={responding}
                          className="flex-1"
                        >
                          {responding ? 'Approving...' : 'Approve'}
                        </Button>
                        <Button
                          onClick={handleReject}
                          disabled={responding}
                          variant="outline"
                          className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                        >
                          {responding ? 'Rejecting...' : 'Reject'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
